import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  FormControl,
  Modal,
  Spinner,
  Table,
  Accordion,
  Tabs,
  Tab
} from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import ApiService from '../../helpers/apiService';
import IconButton from '../common/IconButton';
import QuestionnaireCard from './QuestionnaireCard';
import RiskScoreCard, { RiskScoreData } from './RiskScoreCard';

interface ProfileData {
  userId: string;
  name: string;
  birth_date: string;
  address: string;
  documents: string;
  check_status: string;
  status: string;
  is_manager: boolean;
  is_enabled: boolean;
  kind: string;
  card_list: [];
  account_list: Account[];
  kyc_first_name?: string;
  kyc_last_name?: string;
  kyc_middle_name?: string;
  kyc_dob?: string;
  kyc_gender?: string;
  kyc_country?: string;
  kyc_phone?: string;
  kyc_email?: string;
  kyc_review_status?: string;
}

interface Account {
  account: string;
  currency: string;
  sum: string;
}

interface Limit {
  id: number;
  code: string;
  object_type: number;
  action_type: number;
  limit_type: number;
  value: string;
  remaining: string | null;
  duration: number;
  started_at: string;
}

const STATUS_BADGE: Record<string, string> = {
  ON_HOLD: 'warning',
  COMPLETED: 'success',
  active: 'success',
  blocked: 'danger',
  pending: 'secondary',
};

const kycStatusBadge = (status?: string) => {
  if (!status) return <Badge bg="light" text="dark">N/A</Badge>;
  const bg = STATUS_BADGE[status] ?? 'info';
  return <Badge bg={bg} text={bg === 'warning' ? 'dark' : undefined}>{status}</Badge>;
};

const FieldRow = ({ label, value }: { label: string; value?: string | boolean | null }) => (
  <div className="col-12 col-sm-6 col-md-4">
    <div className="text-muted small">{label}</div>
    <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>
      {value === true ? 'Да' : value === false ? 'Нет' : (value as string) || 'N/A'}
    </div>
  </div>
);

const ProfilePage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [limitsData, setLimitsData] = useState<Limit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('user');
  const [risk, setRisk] = useState<RiskScoreData | null>(null);
  const [riskLabels, setRiskLabels] = useState<string[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);

  // Пароль пользователя: сброс + временный пароль (с обязательной сменой).
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);
  const [showTempModal, setShowTempModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [tempBusy, setTempBusy] = useState(false);
  const [tempDone, setTempDone] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const userId = searchParams.get('userId');
  const phoneQS = searchParams.get('phone');
  const emailQS = searchParams.get('email');
  // Effective lookup key for endpoints that need a user identifier.
  // userId may be a UUID (preferred) or — after resolving via phone/email —
  // populated from the response.
  const lookupId = userId || profileData?.userId || '';

  // Backend (admin-api OpenAPI) typed `userId` as UUID — sending a phone there
  // returns 400. Detect input type and dispatch to the right query param.
  const buildUserListParams = (raw: string): Record<string, string | number> => {
    const v = raw.trim();
    if (!v) return {};
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
    if (isUUID) return { userId: v };
    if (v.includes('@')) return { email: v };
    // Phone — keep digits only (admin-api expects int64).
    const digits = v.replace(/\D/g, '');
    if (digits) return { phone: Number(digits) };
    return {};
  };

  const fetchProfileData = async (raw: string) => {
    const params = buildUserListParams(raw);
    if (Object.keys(params).length === 0) {
      setError('Введите UUID, телефон или email');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await ApiService.get<{ data: ProfileData[] }>(
        '/user/v1/list',
        { params }
      );
      setProfileData(data.data[0]);
    } catch {
      setError('Не удалось загрузить данные пользователя');
    } finally {
      setLoading(false);
    }
  };

  const fetchLimitsData = async (objectType: number) => {
    setLimitsData([]);
    let objectIds = '';
    if (objectType === 0) objectIds = lookupId;
    else if (objectType === 1) objectIds = profileData?.account_list?.map(a => a.account).join(',') || '';
    // @ts-ignore
    else if (objectType === 2) objectIds = profileData?.card_list?.map(c => c?.cardId).join(',') || '';
    if (!objectIds) return;
    try {
      const response = await ApiService.get<{ data: Limit[] }>(
        '/support/v1/limits',
        { params: { objects: objectIds, types: objectType.toString() } }
      );
      setLimitsData(response.data);
    } catch (e) {
      console.error('Error fetching limits:', e);
    }
  };

  useEffect(() => {
    const lookup = userId || phoneQS || emailQS;
    if (lookup) fetchProfileData(lookup);
  }, [userId, phoneQS, emailQS]);

  // KYC scoring + limits depend on the resolved UUID — fire only after profile
  // loads so we don't leak phone numbers into UUID-typed endpoints.
  useEffect(() => {
    const uuid = profileData?.userId;
    if (!uuid) return;
    fetchLimitsData(0);
    // @ts-ignore – kycGetScore returns generic shape
    ApiService.kycGetScore({ user_id: uuid })
      .then((res: any) => {
        setRisk(res?.data?.risk ?? null);
        setRiskLabels(Array.isArray(res?.data?.risk_labels) ? res.data.risk_labels : []);
      })
      .catch(() => {
        setRisk(null);
        setRiskLabels([]);
      });
  }, [profileData?.userId]);

  useEffect(() => {
    if (activeTab === 'user') fetchLimitsData(0);
    else if (activeTab === 'account') fetchLimitsData(1);
    else if (activeTab === 'card') fetchLimitsData(2);
  }, [activeTab, profileData]);

  // Запрос свежих данных из SumSub. Бэкенд (admin-api → kyc → SumSub) подтягивает
  // info / addresses / questionnaire / scoring и публикует Kafka, users.users +
  // user_addresses мирорят. Карточка после успеха перезагружает профиль и скор.
  const handleRefreshFromSumSub = async () => {
    const uuid = profileData?.userId;
    if (!uuid) return;
    setRefreshing(true);
    setRefreshMessage(null);
    try {
      // @ts-ignore — kycRefreshApplicant возвращает {code, data} обёртку
      await ApiService.kycRefreshApplicant(uuid);
      setRefreshMessage('Данные обновлены');
      // Перезагружаем профиль и скор — KYC уже опубликовала Kafka, но локально
      // users.users мог ещё не успеть; на всякий случай тянем повторно.
      await fetchProfileData(uuid);
      // @ts-ignore
      const res: any = await ApiService.kycGetScore({ user_id: uuid });
      setRisk(res?.data?.risk ?? null);
      setRiskLabels(Array.isArray(res?.data?.risk_labels) ? res.data.risk_labels : []);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Не удалось обновить данные';
      setRefreshMessage(msg);
    } finally {
      setRefreshing(false);
      // Сообщение само исчезает через 5 секунд
      setTimeout(() => setRefreshMessage(null), 5000);
    }
  };

  const handleResetPassword = async () => {
    const uuid = profileData?.userId || lookupId;
    if (!uuid) return;
    if (!window.confirm('Сбросить пароль пользователя? Будет запущена процедура восстановления (пользователю придёт код/ссылка).')) return;
    setPwBusy(true);
    setPwMsg(null);
    try {
      await ApiService.userResetPasswordById(uuid);
      setPwMsg({ type: 'success', text: 'Сброс пароля запущен — пользователю отправлен код/ссылка для восстановления.' });
    } catch (e: any) {
      setPwMsg({ type: 'danger', text: e?.response?.data?.message || e?.message || 'Не удалось сбросить пароль' });
    } finally {
      setPwBusy(false);
      setTimeout(() => setPwMsg(null), 8000);
    }
  };

  const generateTempPassword = () => {
    // 12 символов: буквы (вкл. заглавные) + цифры + спецсимвол.
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789@#$%';
    let p = '';
    const rnd = (n: number) => Math.floor(Math.random() * n);
    for (let i = 0; i < 12; i++) p += chars[rnd(chars.length)];
    setTempPassword(p);
  };

  const handleSaveTempPassword = async () => {
    const uuid = profileData?.userId || lookupId;
    if (!uuid || !tempPassword.trim()) return;
    setTempBusy(true);
    setPwMsg(null);
    try {
      await ApiService.userSetTempPassword(uuid, tempPassword.trim());
      setTempDone(true);
    } catch (e: any) {
      setPwMsg({ type: 'danger', text: e?.response?.data?.message || e?.message || 'Не удалось задать временный пароль' });
      setShowTempModal(false);
      setTimeout(() => setPwMsg(null), 8000);
    } finally {
      setTempBusy(false);
    }
  };

  const openTempModal = () => {
    setTempPassword('');
    setTempDone(false);
    setShowTempModal(true);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = buildUserListParams(searchQuery);
    if ('userId' in params) setSearchParams({ userId: String(params.userId) });
    else if ('phone' in params) setSearchParams({ phone: String(params.phone) });
    else if ('email' in params) setSearchParams({ email: String(params.email) });
    else setSearchParams({});
  };

  const renderActionType = (t: number) =>
    ['Общий', 'Пополнение', 'Вывод', 'Платежи картой', 'Перевод', 'Получение'][t] ?? 'Неизвестно';
  const renderLimitType = (t: number) =>
    ['Макс. сумма', 'Макс. кол-во', 'Макс. за операцию', 'Мин. за операцию'][t] ?? 'Неизвестно';
  const renderDuration = (d: number) =>
    d === 86400 ? 'День' : d === 2592000 ? 'Месяц' : d === 0 ? 'День' : 'Неделя';
  const formatNumber = (v: string | number | null) => {
    if (v == null || v === '') return 'N/A';
    return (typeof v === 'string' ? parseFloat(v) : v).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fullName = profileData
    ? [profileData.kyc_last_name, profileData.kyc_first_name, profileData.kyc_middle_name].filter(Boolean).join(' ') || profileData.name || 'N/A'
    : '';

  const LimitsTable = () => (
    <Table hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: '0.82rem' }}>
      <thead className="table-light">
        <tr>
          <th>Мнемоника</th>
          <th>Тип лимита</th>
          <th>Ограничение</th>
          <th>Лимит</th>
          <th>Остаток</th>
          <th>Интервал</th>
          <th>Начало</th>
        </tr>
      </thead>
      <tbody>
        {limitsData.length === 0 ? (
          <tr><td colSpan={7} className="text-muted text-center py-3">Нет данных</td></tr>
        ) : limitsData.map(limit => (
          <tr key={limit.id}>
            <td><code style={{ fontSize: '0.75rem' }}>{limit.code}</code></td>
            <td>{renderActionType(limit.action_type)}</td>
            <td>{renderLimitType(limit.limit_type)}</td>
            <td>{formatNumber(limit.value)}</td>
            <td>{limit.remaining ? formatNumber(limit.remaining) : 'N/A'}</td>
            <td>{renderDuration(limit.duration)}</td>
            <td>{new Date(limit.started_at).toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <form onSubmit={handleSubmit} className="d-flex gap-2">
            <FormControl
              placeholder="Поиск по ФИО, телефону, номеру счёта"
              value={searchQuery}
              onChange={handleSearch}
              size="sm"
            />
            {/* @ts-ignore */}
            <IconButton type="submit" variant="primary" size="sm" disabled={loading} icon="search">
              Поиск{loading && <Spinner as="span" animation="border" size="sm" className="ms-1" />}
            </IconButton>
          </form>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="py-2 small" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !profileData && (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" /><span className="ms-2">Загрузка...</span>
        </div>
      )}

      {profileData && (
        <>
          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
                <div>
                  <h5 className="mb-0">{fullName}</h5>
                  <code className="text-muted small">{profileData.userId}</code>
                </div>
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  {kycStatusBadge(profileData.kyc_review_status)}
                  <Badge bg={profileData.is_enabled ? 'success' : 'danger'}>
                    {profileData.is_enabled ? 'Активен' : 'Заблокирован'}
                  </Badge>
                  {profileData.is_manager && <Badge bg="info">Менеджер</Badge>}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleRefreshFromSumSub}
                    disabled={refreshing || !profileData.userId}
                    title="Перезапросить данные у SumSub и обновить в БД"
                  >
                    {refreshing ? (
                      <><Spinner animation="border" size="sm" className="me-1" />Обновление...</>
                    ) : 'Обновить данные SumSub'}
                  </Button>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={handleResetPassword}
                    disabled={pwBusy || !profileData.userId}
                    title="Запустить восстановление пароля (пользователю придёт код/ссылка)"
                  >
                    {pwBusy ? <><Spinner animation="border" size="sm" className="me-1" />…</> : 'Сбросить пароль'}
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={openTempModal}
                    disabled={!profileData.userId}
                    title="Задать временный пароль с обязательной сменой при первом входе"
                  >
                    Временный пароль
                  </Button>
                </div>
              </div>
              {refreshMessage && (
                <Alert
                  variant={refreshMessage === 'Данные обновлены' ? 'success' : 'warning'}
                  className="py-2 small mb-3"
                  dismissible
                  onClose={() => setRefreshMessage(null)}
                >
                  {refreshMessage}
                </Alert>
              )}
              {pwMsg && (
                <Alert variant={pwMsg.type} className="py-2 small mb-3" dismissible onClose={() => setPwMsg(null)}>
                  {pwMsg.text}
                </Alert>
              )}

              <div className="row g-2">
                <FieldRow label="Фамилия" value={profileData.kyc_last_name} />
                <FieldRow label="Имя" value={profileData.kyc_first_name} />
                <FieldRow label="Отчество" value={profileData.kyc_middle_name} />
                <FieldRow label="Дата рождения" value={profileData.kyc_dob} />
                <FieldRow label="Пол" value={profileData.kyc_gender} />
                <FieldRow label="Страна" value={profileData.kyc_country} />
                <FieldRow label="Телефон" value={profileData.kyc_phone} />
                <FieldRow label="Email" value={profileData.kyc_email} />
                <FieldRow label="Статус" value={profileData.status} />
                <FieldRow label="Менеджер" value={profileData.is_manager} />
                <FieldRow label="Активен" value={profileData.is_enabled} />
              </div>
            </Card.Body>
          </Card>

          {profileData.userId && (
            <RiskScoreCard risk={risk} labels={riskLabels} defaultOpen={false} />
          )}

          {profileData.userId && (
            <QuestionnaireCard userId={profileData.userId} defaultOpen={false} />
          )}

          <Accordion>
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                Счета{' '}
                <Badge bg="secondary" className="ms-2">{profileData.account_list?.length ?? 3}</Badge>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                <Table hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: '0.82rem' }}>
                  <thead className="table-light">
                    <tr><th>Счёт</th><th>Валюта</th><th>Баланс</th></tr>
                  </thead>
                  <tbody>
                    {profileData.account_list?.map((account, i) => (
                      <tr key={i}>
                        <td>
                          <span
                            className="text-primary"
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => navigate(`/transactions?accountId=${account.account}`)}
                          >
                            {account.account}
                          </span>
                        </td>
                        <td>{account.currency}</td>
                        <td>{formatNumber(account.sum)}</td>
                      </tr>
                    )) || (
                      <>
                        <tr><td>Счёт_1</td><td>USD</td><td>{formatNumber('1000.2')}</td></tr>
                        <tr><td>Счёт_2</td><td>ETH</td><td>{formatNumber('0.0025')}</td></tr>
                        <tr><td>Счёт_3</td><td>USDT</td><td>{formatNumber('10.25')}</td></tr>
                      </>
                    )}
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
              <Accordion.Header>Ограничения (Лимиты)</Accordion.Header>
              <Accordion.Body className="p-0">
                <Tabs
                  activeKey={activeTab}
                  onSelect={tab => tab && setActiveTab(tab)}
                  id="profile-tabs"
                  className="px-3 pt-2"
                >
                  <Tab eventKey="user" title="Пользователь"><LimitsTable /></Tab>
                  <Tab eventKey="account" title="Счёт"><LimitsTable /></Tab>
                  <Tab eventKey="card" title="Карта"><LimitsTable /></Tab>
                </Tabs>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </>
      )}

      <Modal show={showDisableModal} onHide={() => setShowDisableModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Выберите аккаунты для отключения</Modal.Title>
        </Modal.Header>
        <Modal.Body>Список аккаунтов для отключения</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowDisableModal(false)}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Временный пароль с обязательной сменой при первом входе */}
      <Modal show={showTempModal} onHide={() => setShowTempModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Временный пароль</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {tempDone ? (
            <Alert variant="success" className="mb-0">
              <div className="mb-2">Временный пароль установлен. Пользователь обязан сменить его при первом входе.</div>
              <div className="small text-muted">Передайте пароль пользователю:</div>
              <code className="d-block fs-6 mt-1" style={{ wordBreak: 'break-all' }}>{tempPassword}</code>
            </Alert>
          ) : (
            <>
              <div className="text-muted small mb-2">
                Задайте временный пароль или сгенерируйте. После сохранения пользователь
                сможет войти с ним один раз и будет обязан сменить пароль (флаг must_change).
              </div>
              <div className="d-flex gap-2">
                <FormControl
                  type="text"
                  placeholder="Временный пароль"
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                />
                <Button variant="outline-secondary" onClick={generateTempPassword}>Сгенерировать</Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {tempDone ? (
            <Button variant="primary" size="sm" onClick={() => setShowTempModal(false)}>Готово</Button>
          ) : (
            <>
              <Button variant="outline-secondary" size="sm" onClick={() => setShowTempModal(false)}>Отмена</Button>
              <Button variant="primary" size="sm" onClick={handleSaveTempPassword} disabled={tempBusy || !tempPassword.trim()}>
                {tempBusy ? <Spinner animation="border" size="sm" className="me-1" /> : null}Сохранить
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProfilePage;
